import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import { Alert, Button, Label, Modal, ModalFooter } from "@/components/ui";
import { QRCodeSVG } from "qrcode.react";
import { buildKioskCredentialsQrValue } from "@/lib/kioskCredentialsQr";

interface DeviceCredentialsModalProps {
  isOpen: boolean;
  title: string;
  description: string;
  kioskCode: string;
  password: string;
  onClose: () => void;
}

export function DeviceCredentialsModal({
  isOpen,
  title,
  description,
  kioskCode,
  password,
  onClose,
}: DeviceCredentialsModalProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("kiosk");
  const qrValue = useMemo(
    () => buildKioskCredentialsQrValue(kioskCode, password),
    [kioskCode, password],
  );

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <ModalFooter>
          <Button type="button" onClick={onClose}>
            {tCommon("close")}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-5">
        <Alert variant="success">{description}</Alert>

        <div className="grid gap-5 md:grid-cols-2">
          <div className="space-y-4">
            <div>
              <Label as="p" spacing="relaxed">
                {t("credentialsModal.kioskCode")}
              </Label>
              <ClickToCopyId value={kioskCode} />
            </div>
            <div>
              <Label as="p" spacing="relaxed">
                {t("credentialsModal.password")}
              </Label>
              <ClickToCopyId value={password} />
            </div>
          </div>
          <div className="flex flex-col items-center justify-center rounded-lg border border-surface-200 bg-surface-50 p-4">
            <div className="overflow-hidden rounded-lg ring-1 ring-surface-200">
              <QRCodeSVG value={qrValue} size={180} includeMargin />
            </div>
            <p className="mt-3 text-center text-xs text-surface-500">
              {t("credentialsModal.qrHelp")}
            </p>
          </div>
        </div>

        <Alert variant="warning" title={t("credentialsModal.securityWarningTitle")}>
          {t("credentialsModal.securityWarningBody")}
        </Alert>
      </div>
    </Modal>
  );
}
